import React, { useMemo, useState } from 'react';
import { ArrowRight, FileText, Loader2 } from 'lucide-react';
import accidentalIcon from '@/assets/icons/returns/accidental-icon.svg';
import extendedIcon from '@/assets/icons/returns/extended-icon.svg';
// import returnIcon from '@/assets/icons/returns/returns-icon.svg';
import type { ProductData } from '@/components/scan-qr/ConfirmationPage';
import type { MerchantTerms } from '@/service/returnService';

interface ActionPageProps {
    productData: ProductData;
    merchantTerms?: MerchantTerms;
    onSelectAction: (action: string) => void;
}

const actions = [
    {
        id: 'accidental-damage',
        icon: accidentalIcon,
        title: 'Accidental Damage Care',
        description: 'Protect your product against accidental damage and unexpected issues.',
    },
    {
        id: 'extended-warranty',
        icon: extendedIcon,
        title: 'Extended Warranty',
        description: "Extend your product's warranty beyond the standard coverage period.",
    },
    /*
    {
        id: 'return-product',
        icon: returnIcon,
        title: 'Return product',
        description: 'Request a return by sharing the issue with your product.',
    },
    */
];

const ActionPage: React.FC<ActionPageProps> = ({ productData, merchantTerms, onSelectAction }) => {
    const [loadingActionId, setLoadingActionId] = useState<string | null>(null);

    const handleActionSelect = async (actionId: string) => {
        setLoadingActionId(actionId);
        await new Promise(resolve => setTimeout(resolve, 500));
        onSelectAction(actionId);
    };

    // Filter actions based on available terms — only show AD/EW if corresponding terms exist
    const filteredActions = useMemo(() => {
        return actions.filter((action) => {
            if (action.id === 'accidental-damage') {
                return merchantTerms?.['Accidental Damage'] && merchantTerms['Accidental Damage'].length > 0;
            }
            if (action.id === 'extended-warranty') {
                return merchantTerms?.['Extended Warranty'] && merchantTerms['Extended Warranty'].length > 0;
            }
            return true; // Always show return-product
        });
    }, [merchantTerms]);

    // Generate a preview URL for the product photo
    const photoPreviewUrl = useMemo(() => {
        if (productData.productPhoto) {
            return URL.createObjectURL(productData.productPhoto);
        }
        return null;
    }, [productData.productPhoto]);

    return (
        <div className="tw-h-full tw-flex tw-flex-col">
            <div className="tw-flex-1 tw-flex tw-flex-col tw-gap-6 sm:tw-px-4">

                {/* Header */}
                <div className="tw-text-center tw-mb-2">
                    <h1 className="tw-text-xl sm:tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-2">
                        What would you like to<br />do next?
                    </h1>
                    <p className="tw-text-gray-500 tw-text-sm sm:tw-text-base">
                        Choose how you'd like to proceed for this product.
                    </p>
                </div>

                {/* Product Card */}
                <div className="tw-bg-gray-50 tw-rounded-2xl tw-overflow-hidden">
                    <div className="tw-flex tw-gap-4 tw-p-4">
                        {/* Product Photo */}
                        {photoPreviewUrl ? (
                            <div className="tw-w-28 tw-h-28 tw-rounded-xl tw-overflow-hidden tw-flex-shrink-0">
                                <img
                                    src={photoPreviewUrl}
                                    alt={productData.productName}
                                    className="tw-w-full tw-h-full tw-object-cover"
                                />
                            </div>
                        ) : (
                            <div className="tw-w-28 tw-h-28 tw-rounded-xl tw-bg-gray-200 tw-flex-shrink-0 tw-flex tw-items-center tw-justify-center">
                                <span className="tw-text-gray-400 tw-text-xs">No photo</span>
                            </div>
                        )}

                        {/* Product Info */}
                        <div className="tw-flex tw-flex-col tw-justify-center tw-gap-1.5 tw-min-w-0">
                            <h3 className="tw-font-semibold tw-text-gray-900 tw-text-base tw-leading-tight tw-line-clamp-4">
                                {productData.productName || 'Unnamed Product'}
                            </h3>
                            <p className="tw-text-sm tw-text-gray-600">
                                Order ID: <span className="tw-font-medium">{productData.orderId}</span>
                            </p>
                        </div>
                    </div>

                    {productData.purchaseInvoice && (
                        <div className="tw-px-4 tw-pb-4">
                            <div className="tw-flex tw-items-center tw-gap-1.5 tw-bg-white tw-bg-opacity-50 tw-p-2 tw-rounded-lg tw-border tw-border-gray-200/50">
                                <FileText className="tw-w-4 tw-h-4 tw-text-gray-500 tw-flex-shrink-0" strokeWidth={1.5} />
                                <span className="tw-text-sm tw-text-gray-600 tw-truncate tw-flex-1">
                                    {productData.purchaseInvoice.name}
                                </span>
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (productData.purchaseInvoice) {
                                            const url = URL.createObjectURL(productData.purchaseInvoice);
                                            window.open(url, '_blank');
                                        }
                                    }}
                                    className="tw-text-cyan-500 tw-text-sm tw-font-medium tw-bg-transparent tw-border-none tw-cursor-pointer tw-outline-none hover:tw-underline tw-flex-shrink-0"
                                >
                                    view
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Action cards */}
                <div className="tw-flex tw-flex-col tw-gap-3">
                    {filteredActions.map((action) => (
                        <button
                            key={action.id}
                            type="button"
                            onClick={() => handleActionSelect(action.id)}
                            disabled={loadingActionId !== null}
                            className="tw-w-full tw-flex tw-items-center tw-gap-4 tw-p-4 tw-bg-white tw-border tw-border-gray-200 tw-rounded-xl tw-cursor-pointer tw-outline-none tw-transition-colors hover:tw-bg-gray-50 tw-text-left tw-group disabled:tw-opacity-60 disabled:tw-cursor-not-allowed"
                            style={{ borderLeftWidth: '3px', borderLeftColor: '#22d3ee' }}
                        >
                            <div className="tw-flex-shrink-0">
                                <img src={action.icon} alt="" className="tw-w-6 tw-h-6" />
                            </div>
                            <div className="tw-flex-1 tw-min-w-0">
                                <h3 className="tw-font-semibold tw-text-gray-900 tw-text-sm tw-mb-0.5">
                                    {action.title}
                                </h3>
                                <p className="tw-text-xs tw-text-gray-500 tw-leading-relaxed">
                                    {action.description}
                                </p>
                            </div>
                            {loadingActionId === action.id ? (
                                <Loader2 className="tw-w-5 tw-h-5 tw-text-gray-400 tw-flex-shrink-0 tw-animate-spin" strokeWidth={1.5} />
                            ) : (
                                <ArrowRight className="tw-w-5 tw-h-5 tw-text-gray-400 tw-flex-shrink-0 group-hover:tw-text-gray-600 tw-transition-colors" strokeWidth={1.5} />
                            )}
                        </button>
                    ))}
                </div>

                {/* Footer text */}
                <p className="tw-text-center tw-text-gray-500 tw-text-sm tw-leading-relaxed tw-mt-auto tw-pb-4">
                    You'll be able to manage and track<br />
                    everything from your Protega dashboard.
                </p>
            </div>
        </div>
    );
};

export default ActionPage;
